import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Our <span className="text-burnt-orange">Story</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Dedicated to capturing life's most precious moments through the lens of creativity and innovation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}

      <section className="py-20 bg-dark-gray">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            {/* Image Section */}
            <motion.div
              className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-10"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-burnt-orange to-yellow-500 rounded-2xl opacity-75 blur"></div>
                <motion.img
                  src="/who-we-are.jpg"
                  alt="Our Team"
                  className="relative rounded-2xl w-full h-auto shadow-2xl transition-transform duration-500 ease-in-out group-hover:scale-105"
                />
              </div>
            </motion.div>

            {/* Text Section */}
            <motion.div
              className="w-full md:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                Who We <span className="text-burnt-orange">Are</span>
              </h2>
              <p className="text-gray-300 mb-6">
                SAMFREN OJO MEDIA began in 2022 with a simple drive. A passion for storytelling and a deep love for visual creativity. What started as a one-man freelance journey has grown into a dedicated production company focused on telling meaningful stories that connect with real people.
              </p>
              <p className="text-gray-300 mb-6">
                Over the years, we’ve had the opportunity to work with NGOs, international organizations, and various brands across Nigeria, creating visual content that speaks clearly and leaves a lasting impact.
              </p>
              <p className="text-gray-300 mb-6">
                We care about the details. We care about the story. And most of all, we care about helping our clients communicate with excellence and authenticity through film and media.
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-8">
                <motion.div
                  className='p-2 rounded-2xl'
                  whileHover={{ scale: 1.05, boxShadow: "0px 10px 30px rgba(255, 85, 34, 0.2)" }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <h3 className="text-4xl font-bold text-burnt-orange mb-2">400+</h3>
                  <p className="text-gray-300">Projects Completed</p>
                </motion.div>
                <motion.div
                  className='p-2 rounded-2xl'
                  whileHover={{ scale: 1.05, boxShadow: "0px 10px 30px rgba(255, 255, 255, 0.2)" }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <h3 className="text-4xl font-bold text-burnt-orange mb-2">100%</h3>
                  <p className="text-gray-300">Client Satisfaction</p>
                </motion.div>
                <motion.div
                  className='p-2 rounded-2xl'
                  whileHover={{ scale: 1.05, boxShadow: "0px 10px 30px rgba(255, 255, 255, 0.2)" }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <h3 className="text-4xl font-bold text-burnt-orange mb-2">5+</h3>
                  <p className="text-gray-300">Years Experience</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-dark-gray/80">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <div className="w-20 h-1 bg-burnt-orange mx-auto"></div>
            <p className="text-gray-300 mb-6">
              At SAMFREN OJO MEDIA, our work is guided by a set of values that define how we create, collaborate, and show up for every project. These are not just buzzwords—they’re principles we live by.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{
              title: "Creativity",
              description: "We don’t just make videos, we tell stories. Creativity is at the heart of everything we do, from concept development to final edit. We believe in pushing boundaries and finding unique ways to bring ideas to life. Every frame we shoot is intentional, and every scene is a chance to connect deeply with an audience.",
              icon: "💡"
            }, {
              title: "Excellence",
              description: "For us, good is never enough. Irrespective of the project, we strive to deliver top-quality work that speaks volumes. Excellence shows in how we plan, how we shoot, how we edit, and how we treat our clients.",
              icon: "✨"
            }, {
              title: "Integrity",
              description: "We do what we say, and we say what we mean. With timelines, budgets, and client relationships, we keep it real and transparent. We believe in honesty, accountability, and standing by our word even when no one’s watching.",
              icon: "🤝"
            }, {
              title: "Passion",
              description: "This isn’t just a job for us, it’s what we love. We wake up excited to create, tell stories, and make things that matter. Passion fuels our late nights, drives our problem-solving, and keeps us going even when the work gets tough.",
              icon: "❤️"
            }, {
              title: "Collaboration",
              description: "Great work is never done alone. We believe in building real connections with our clients, our crew, and our audience. Every project is a partnership, and we’re big on listening, learning, and growing with every shoot.",
              icon: "👥"
            }, {
              title: "Innovation",
              description: "The media landscape is always changing, and so are we. We constantly explore new tools, techniques, and technologies to stay ahead of the curve. We’re always evolving.",
              icon: "🚀"
            }].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-900/50 p-8 rounded-lg hover:bg-gray-800/50 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-2xl"
              >
                <motion.div
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="text-4xl mb-4"
                >
                  {value.icon}
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}

      <section className="py-20 bg-dark-gray/80">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Meet Our Team</h2>
            <div className="w-20 h-1 bg-burnt-orange mx-auto"></div>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Our team of talented professionals brings together diverse skills and experiences to deliver exceptional results.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[{
              name: "Samuel Ojo",
              role: "Media Production Director",
              image: "/founder2.jpg"
            }, {
              name: "Oluwafikayo Oyewole",
              role: "Production Assistant",
              image: "/Oluwafikayo-Oyewole.jpg"
            }, {
              name: "Ihuoma Ndu-Eluwa",
              role: "Marketing Manager",
              image: "/Ihuoma-Ndu-Eluwa.jpg"
            }, {
              name: "David Job",
              role: "Cinematographer",
              image: "/David-Job.jpg"
            }, {
              name: "Adeiza Sanni",
              role: "Creative Designer",
              image: "/adeiza-sanni.jpg"
            }, {
              name: "Eromosele Joshua",
              role: "Documentary Videographer | Operations Officer",
              image: "/eromosele-joshua.jpg"
            }, {
              name: "Esther Ali",
              role: "Copywriter / Executive Assistant",
              image: "/esther-ali.jpg"
            }, {
              name: "Gbolahan Ogunsolu",
              role: "Media Producer / Event Photographer",
              image: "/gbolahan-ogunsolu.jpg"
            }, {
              name: "Harrison Esthon",
              role: "Videographer and Livestream Producer",
              image: "/harrison-esthon.jpg"
            }, {
              name: "John Francis Nwokoro",
              role: "Media Production Assistant",
              image: "/john-francis.jpg"
            }, {
              name: "Olawuyi Oyewusi",
              role: "Project Synergy and Technical Support",
              image: "/olawuyi-oyewusi.jpg"
            }].map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <div className="relative mb-6 overflow-hidden rounded-lg group hover:bg-gray-800/50 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-2xl">
                  <div className="aspect-w-1 aspect-h-1">
                    <motion.img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                      whileHover={{ scale: 1.05 }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <div className="text-left">
                      <h4 className="text-white font-semibold text-lg">{member.name}</h4>
                      <p className="text-burnt-orange text-sm">{member.role}</p>
                      {index === 0 && (
                        <Link to={""} className="text-gray-300 mt-2 underline">
                          View Profile
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                <p className="text-gray-400">{member.role}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, boxShadow: "0px 10px 20px rgba(255, 85, 34, 0.3)" }}
              whileTap={{ scale: 0.98 }}
              className="inline-block"
            >
              <Link 
                to="/vacancies" 
                className="inline-block bg-burnt-orange hover:bg-burnt-orange/90 text-white font-semibold py-3 px-8 rounded-md transition-colors text-lg"
              >
                Join Our Team
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Work With Us?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's create something amazing together. Get in touch with our team to discuss your next project.
            </p>
            <a
              href="/contact"
              className="inline-block bg-burnt-orange hover:bg-burnt-orange/90 text-white font-semibold py-3 px-8 rounded-md transition-colors"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
